class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  getLineRow(line) {
    return (
      document.getElementById(`CartItem-${line}`) ||
      document.getElementById(`CartDrawer-Item-${line}`)
    );
  }

  onCartUpdate() {
    // Cart page only — drawer overrides this
    if (!document.getElementById('main-cart-items')) return;

    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty) this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    const sections = [];
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      });
    }
    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '.shopify-section',
    });
    if (document.getElementById('cart-live-region-text')) {
      sections.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      });
    }
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents',
      });
    }
    return sections;
  }

  getSectionsUrl() {
    return (
      (window.Shopify && Shopify.routes && Shopify.routes.root) ||
      window.location.pathname ||
      '/'
    );
  }

  async fetchSectionsFallback(sectionIds) {
    const root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) ||
      (window.routes && window.routes.cart_url
        ? window.routes.cart_url.replace(/\/cart\/?$/, '/')
        : '/');
    const response = await fetch(`${root}?sections=${sectionIds.join(',')}`);
    if (!response.ok) throw new Error(`sections fallback ${response.status}`);
    return response.json();
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const row = this.getLineRow(line);
    if (row && row.dataset.llEngravingFee === 'true') {
      // Fee lines are controlled by the parent product qty
      this.disableLoading(line);
      return;
    }

    const engravingLinkId = row && row.dataset.llEngravingId;
    const sectionsToRender = this.getSectionsToRender();
    const sectionIds = sectionsToRender.map((section) => section.section).filter(Boolean);
    const body = JSON.stringify({
      line: Number(line),
      quantity: Number(quantity),
      sections: sectionIds,
      sections_url: this.getSectionsUrl(),
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then(async (state) => {
        let parsedState = JSON.parse(state);

        if (parsedState.errors) {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) ||
            document.getElementById(`Drawer-quantity-${line}`);
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Keep linked engraving fee line qty in sync with the parent product
        if (
          engravingLinkId &&
          window.llEngravingCart &&
          typeof window.llEngravingCart.syncFeeForLink === 'function'
        ) {
          try {
            const synced = await window.llEngravingCart.syncFeeForLink(
              engravingLinkId,
              Number(quantity),
              sectionIds,
              this.getSectionsUrl()
            );
            if (synced && !synced.errors) {
              parsedState = synced;
            }
          } catch (e) {
            console.error('[ll-engraving] fee sync failed', e);
          }
        }

        // If section HTML missing (common on market URLs), fetch sections separately
        const needed = sectionIds;
        const hasAll =
          parsedState.sections && needed.every((id) => parsedState.sections[id]);
        if (!hasAll) {
          try {
            parsedState.sections = Object.assign(
              {},
              parsedState.sections || {},
              await this.fetchSectionsFallback(needed)
            );
          } catch (e) {
            console.error(e);
          }
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        }

        sectionsToRender.forEach((section) => {
          const sectionHtml = parsedState.sections && parsedState.sections[section.section];
          if (!sectionHtml) return;

          if (
            section.id === 'cart-icon-bubble' &&
            document.querySelector('#cart-icon-bubble.ll-header__icon-btn')
          ) {
            const drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.updateHeaderCartIcon === 'function') {
              drawer.updateHeaderCartIcon(sectionHtml);
              return;
            }
          }

          const host = document.getElementById(section.id);
          if (!host) return;
          const elementToReplace = host.querySelector(section.selector) || host;
          const inner = this.getSectionInnerHTML(sectionHtml, section.selector);
          if (inner == null) return;
          elementToReplace.innerHTML = inner;
        });

        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = '';
        if (
          quantityElement &&
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value, 10)
        ) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
          const focusEl = lineItem.querySelector(`[name="${name}"]`);
          if (cartDrawerWrapper) trapFocus(cartDrawerWrapper, focusEl);
          else focusEl.focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          const empty = cartDrawerWrapper.querySelector('.drawer__inner-empty');
          const emptyLink = empty && empty.querySelector('a');
          if (empty && emptyLink) trapFocus(empty, emptyLink);
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          const nameEl = document.querySelector('.cart-item__name');
          if (nameEl) trapFocus(cartDrawerWrapper, nameEl);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      })
      .catch((e) => {
        console.error(e);
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors =
          document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const text = lineItemError.querySelector('.cart-item__error-text');
      if (text) text.innerHTML = message || '';
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;

    cartStatus.setAttribute('aria-hidden', false);
    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove('hidden')
    );

    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
